'use client';

import { useGSAP } from '@gsap/react';
import clsx from 'clsx';
import { FC, useRef } from 'react';

import { AWARDS_VIDEOS } from '@/components/Awards/assets';
import { ServicesAwardsSectionDto } from '@/components/ServicesLobbyPage/ServicesAwardsSection/types';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import { useLazyVideoSource } from '@/hooks/useLazyVideoSource';
import { useViewportVideosPlayback } from '@/hooks/useViewportVideosPlayback';
import { gsap } from '@/lib/gsap';

import css from './style.module.scss';

interface Props {
	data: ServicesAwardsSectionDto;
}

const ServicesAwardsSection: FC<Props> = ({ data }) => {
	const sectionRef = useRef<HTMLElement>(null);
	const titleRef = useRef<HTMLHeadingElement>(null);
	const descriptionRef = useRef<HTMLDivElement>(null);

	const shouldLoadVideos = useLazyVideoSource(sectionRef);
	useViewportVideosPlayback([sectionRef]);

	useGSAP(
		() => {
			const titleEl = titleRef.current;
			const descriptionEl = descriptionRef.current;
			const titleLines = titleEl?.querySelectorAll('p');
			const descriptionLines = descriptionEl?.querySelectorAll('p');

			if (!titleEl) return;

			const titleTargets = titleLines && titleLines.length ? Array.from(titleLines) : [titleEl];

			gsap.set(titleTargets, {
				opacity: 0,
				y: 40,
				filter: 'blur(10px)'
			});

			gsap.to(titleTargets, {
				opacity: 1,
				y: 0,
				filter: 'blur(0px)',
				duration: 1.1,
				ease: 'power3.out',
				stagger: titleTargets.length > 1 ? 0.15 : 0,
				scrollTrigger: {
					trigger: titleEl,
					start: 'top 80%',
					toggleActions: 'play none none reverse'
				}
			});

			if (!descriptionEl) return;

			const descriptionTargets =
				descriptionLines && descriptionLines.length
					? Array.from(descriptionLines)
					: [descriptionEl];

			gsap.set(descriptionTargets, {
				opacity: 0,
				y: 30,
				filter: 'blur(10px)'
			});

			gsap.to(descriptionTargets, {
				opacity: 1,
				y: 0,
				filter: 'blur(0px)',
				duration: 1,
				delay: 0.3,
				ease: 'power3.out',
				stagger: descriptionTargets.length > 1 ? 0.1 : 0,
				scrollTrigger: {
					trigger: descriptionEl,
					start: 'top 85%',
					toggleActions: 'play none none reverse'
				}
			});
		},
		{ scope: sectionRef }
	);

	return (
		<section ref={sectionRef} className={css.wrapper}>
			<div className={clsx(css.content, 'container')}>
				<div className={css.textWrapper}>
					<h2 ref={titleRef} className={css.title}>
						<StrapiRichContent content={data.title} />
					</h2>
					<Typography ref={descriptionRef} className={css.description} variant="body-m" tag="div">
						<StrapiRichContent content={data.description} />
					</Typography>
				</div>

				<ul className={css.awardsList}>
					{data.awards.map(award => (
						<li key={award.id} className={css.awardsItem}>
							<video loop muted playsInline preload="none" controls={false}>
								{shouldLoadVideos
									? AWARDS_VIDEOS[award.type]?.map((source, index) => (
											<source
												key={`video_${index}`}
												src={source}
												type={
													source.includes('mov') ? 'video/quicktime; codecs="hvc1"' : 'video/webm'
												}
											/>
										))
									: null}
							</video>
						</li>
					))}
				</ul>
			</div>
		</section>
	);
};

export default ServicesAwardsSection;
